import { Global, css } from '@emotion/react';
import { useBreakpointValue } from '@hover/blueprint';
import type { ReactElement } from 'react';
import React, { createContext, useCallback, useMemo, useState } from 'react';

import { getOrCreateSplitKey } from 'src/modules/app/components/Providers/Telemetry/Split/';
import { useWebViewListener } from 'src/modules/homeownerApp/hooks/useWebViewListener';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import type { AiGeneratedImage, User } from 'src/shared/graphql/generated';
import { useProfileQuery } from 'src/shared/graphql/generated';
import { useCurrentPage } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { moduleViewed } from 'src/shared/telemetry';

import { getUserSession, useUserSession } from '../hooks/useUserSession';

const hideIntercomCSS = css`
  .intercom-lightweight-app {
    display: none !important;
  }
`;

export const HomeownerAppMenuNames = {
  ORIGINAL_IMAGE_MORE_ACTIONS: 'original_image_more_actions',
  PROFILE: 'profile',
  PROJECT_MORE_ACTIONS: 'project_more_actions',
  SIGN_IN: 'sign_in',
  UPLOAD_PHOTO: 'upload_photo',
  WELCOME_BACK: 'welcome_back',
  WELCOME_TO: 'welcome_to',
  ADD_TO_PROJECT: 'add_to_project',
  NEW_PROJECT: 'new_project',
  EDIT_PROJECT: 'edit_project',
  ADD_SAVED_TO_PROJECT: 'add_saved_to_project',
  ADD_IDEAS_TO_PROJECT: 'add_ideas_to_project',
} as const;

const homeownerAppMenuTrackIds = {
  [HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS]:
    'original-image-more-bottom-sheet',
  [HomeownerAppMenuNames.PROFILE]: 'profile-bottom-sheet',
  [HomeownerAppMenuNames.PROJECT_MORE_ACTIONS]:
    'project-more-actions-bottom-sheet',
  [HomeownerAppMenuNames.SIGN_IN]: 'log-in-sign-up-bottom-sheet',
  [HomeownerAppMenuNames.UPLOAD_PHOTO]: 'add-photo-bottom-sheet',
  [HomeownerAppMenuNames.WELCOME_BACK]: 'welcome-back-module',
  [HomeownerAppMenuNames.WELCOME_TO]: 'welcome-module',
  [HomeownerAppMenuNames.ADD_TO_PROJECT]: 'add-to-project-bottom-sheet',
  [HomeownerAppMenuNames.NEW_PROJECT]: 'new-project-bottom-sheet',
  [HomeownerAppMenuNames.EDIT_PROJECT]: 'edit-project-bottom-sheet',
  [HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT]:
    'add-saved-to-project-bottom-sheet',
  [HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT]:
    'add-ideas-to-project-bottom-sheet',
};

export type HomeownerAppMenuNamesValues =
  (typeof HomeownerAppMenuNames)[keyof typeof HomeownerAppMenuNames];
export interface HomeownerAppTrackingData {
  device_type: string;
  feature: FeatureTracking;
  org_id?: number;
  page_or_screen_name: string;
  user_id?: number;
  user_authenticated?: boolean;
  session_id?: string;
  split_key: string;
}

export interface HomeownerAppContextProps {
  loggedIn: boolean;
  user?: Partial<User>;
  imageFileToUpload?: File;
  setImageFileToUpload: (file?: File) => void;
  aiImageToAddToProject?: AiGeneratedImage;
  setAiImageToAddToProject: (aiImage?: AiGeneratedImage) => void;
  currentMenu: HomeownerAppMenuNamesValues | null;
  setCurrentMenu: (menu: HomeownerAppMenuNamesValues | null) => void;
  trackingData: HomeownerAppTrackingData;
}

export const initialContext = {
  loggedIn: false,
  user: undefined,
  currentMenu: null,
  setImageFileToUpload: () => null,
  setAiImageToAddToProject: () => null,
  setCurrentMenu: () => null,
  trackingData: {
    device_type: '',
    feature: FeatureTracking.HOMEOWNER_APP,
    page_or_screen_name: '',
    split_key: '',
  },
};

type CurrentMenu = HomeownerAppMenuNamesValues | null;

const homeownerAppContext =
  createContext<HomeownerAppContextProps>(initialContext);

const HomeownerAppProvider = ({ children }: { children: ReactElement }) => {
  useAuthExperience();
  useWebViewListener();

  const [currentMenu, setCurrentMenuState] = useState<CurrentMenu>(null);
  const [imageFileToUpload, setImageFileToUpload] = useState<
    File | undefined
  >();
  const [aiImageToAddToProject, setAiImageToAddToProject] = useState<
    AiGeneratedImage | undefined
  >();

  const { data: profileData } = useProfileQuery({ errorPolicy: 'all' });
  const currentUser = profileData?.profile?.user as User;
  const userSessionId = useUserSession({ userId: currentUser?.id });

  const mobileOrDesktop = useBreakpointValue({
    base: 'mobile',
    tablet: 'tablet',
    desktop: 'desktop',
  });

  const pageName = useCurrentPage() || '';

  // Sometimes users with expired sessions can trigger some
  // events before the user session is renewed, so we need
  // to check if the stored session is reliable for tracking.
  const isSessionReliable = useCallback(
    () =>
      !userSessionId
        ? false
        : getUserSession()?.expiresAt! > new Date().toISOString(),
    [userSessionId],
  );

  const trackingData = useMemo(
    (): HomeownerAppTrackingData => ({
      device_type: mobileOrDesktop,
      feature: FeatureTracking.HOMEOWNER_APP,
      user_id: currentUser?.id,
      org_id: currentUser?.orgs?.[0]?.id,
      page_or_screen_name: pageName,
      user_authenticated: !!currentUser,
      session_id: isSessionReliable() ? userSessionId : undefined,
      split_key: typeof window !== 'undefined' ? getOrCreateSplitKey() : '',
    }),
    [currentUser, isSessionReliable, mobileOrDesktop, pageName, userSessionId],
  );

  const setCurrentMenu = useCallback(
    (menu: CurrentMenu) => {
      if (menu) {
        moduleViewed({
          ...trackingData,
          track_id: homeownerAppMenuTrackIds[menu],
        });
      }

      setCurrentMenuState(menu);
    },
    [trackingData],
  );

  const value = useMemo(
    () => ({
      user: currentUser,
      loggedIn: !!currentUser,
      imageFileToUpload,
      setImageFileToUpload,
      aiImageToAddToProject,
      setAiImageToAddToProject,
      currentMenu,
      setCurrentMenu,
      trackingData,
    }),
    [
      aiImageToAddToProject,
      currentMenu,
      currentUser,
      imageFileToUpload,
      setCurrentMenu,
      trackingData,
    ],
  );

  return (
    <homeownerAppContext.Provider value={value}>
      {mobileOrDesktop !== 'desktop' && <Global styles={hideIntercomCSS} />}
      {children}
    </homeownerAppContext.Provider>
  );
};

export { HomeownerAppProvider, homeownerAppContext };
